import * as locales from "./locales";
import { I18n, getLocale } from "@devforce/trailhead-client-locale";
const i18n = new I18n({ helpers: locales });

function isYesterday(date: Date, today: Date) {
  const differenceMS = today.getTime() - date.getTime();
  const differenceDays = differenceMS / (1000 * 3600 * 24);
  return differenceDays > 1 && differenceDays < 2;
}

export function timestamp(createdAt: string) {
  const date = new Date(createdAt);
  const today = new Date();

  const isToday = date.toDateString() === today.toDateString();
  const isThisYear = date.getFullYear() === today.getFullYear();
  const yesterday = isYesterday(date, today);

  const timeFormatter = new Intl.DateTimeFormat(getLocale(), {
    month: isToday || yesterday ? undefined : "short",
    day: isToday || yesterday ? undefined : "numeric",
    year: isToday || yesterday || isThisYear ? undefined : "numeric",
    hour: "numeric",
    minute: "numeric",
  });
  const dateTime = timeFormatter.format(date);

  if (isToday) {
    return i18n.t("helpers", "todayTimestamp", { time: dateTime });
  } else if (yesterday) {
    return i18n.t("helpers", "yesterdayTimestamp", { time: dateTime });
  }

  return dateTime;
}

export function lastActivity(isoString?: string | null) {
  if (isoString) {
    const formatter = new Intl.DateTimeFormat(getLocale(), {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const date = new Date(isoString);
    const formatted = formatter.format(date);
    return i18n.t("helpers", "lastActivity", { date: formatted });
  }

  return "";
}

export function isBefore(date: Date, dateToCompare: Date): boolean {
  return date < dateToCompare;
}

export function formatDuration(seconds: number) {
  // Calculate hours, minutes, and remaining seconds
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  // Format the remaining seconds to always have two digits
  const formattedSeconds = remainingSeconds.toString().padStart(2, "0");

  // Determine the correct label for hours and minutes
  if (hours > 0) {
    const hourLabelKey = hours === 1 ? "durationHour" : "durationHours";
    const formattedMinutes = minutes.toString().padStart(2, "0");
    return i18n.t("helpers", hourLabelKey, {
      duration: `${hours}:${formattedMinutes}:${formattedSeconds}`,
    });
  } else {
    const minuteLabelKey = minutes === 1 ? "durationMinute" : "durationMinutes";
    return i18n.t("helpers", minuteLabelKey, {
      duration: `${minutes}:${formattedSeconds}`,
    });
  }
}

export function isOverdue(dueDate: string): boolean {
  if (!dueDate) return false;

  // Get user's timezone
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Get current time in user's timezone
  const now = new Date().toLocaleString("en-US", { timeZone: userTimeZone });
  const nowDate = new Date(now);
  nowDate.setHours(0, 0, 0, 0);

  // Parse the due date and convert to user's timezone
  const dateOnly = dueDate.split("T")[0];
  const [year, month, day] = dateOnly.split("-").map(Number);
  const dueDateObj = new Date(year, month - 1, day);
  const dueDateInTz = new Date(
    dueDateObj.toLocaleString("en-US", { timeZone: userTimeZone })
  );

  // Set to midnight of the next day (end of due date)
  const dueAtMidnight = new Date(dueDateInTz);
  dueAtMidnight.setHours(0, 0, 0, 0);
  dueAtMidnight.setDate(dueAtMidnight.getDate() + 1);

  return nowDate >= dueAtMidnight;
}

/**
 * Formats a date string with consistent short format (e.g., "Jan 15, 2024")
 * @param dateString ISO date string, date-only string (YYYY-MM-DD), or various other date formats
 * @returns Formatted date string, or original string if parsing fails
 */
export function formatShortDate(dateString: string): string {
  if (!dateString) return "";

  let dateObj: Date;

  if (dateString.includes("-") && dateString.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
    const dateOnly = dateString.split("T")[0];
    const [year, month, day] = dateOnly.split("-").map(Number);

    // Confirm this is a valid date before creating Date object
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return dateString;
    }

    dateObj = new Date(year, month - 1, day);

    // Confirm the created date matches input
    if (
      dateObj.getFullYear() !== year ||
      dateObj.getMonth() !== month - 1 ||
      dateObj.getDate() !== day
    ) {
      return dateString;
    }
  } else {
    // Fall back to native Date parsing for other formats
    dateObj = new Date(dateString);
  }

  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    // If parsing fails, return the original string
    return dateString;
  }

  const formatter = new Intl.DateTimeFormat(getLocale(), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return formatter.format(dateObj);
}
